import React, { FunctionComponent as FC, useState } from 'react';
import { Typography, Grid, Box, useTheme } from '@material-ui/core';
import { navigate, useLocation, useParams } from '@reach/router';
import { Layout } from '../../pge-plus-common/Layout';
import colors from '../../../themes/main-colors';
import useContentMgmt from '../../../hooks/useContentMgmt';
import ROUTES from '../../../routes';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { useInstallerJobDetailsQuery } from '../../../hooks/pge-plus/useInstallerJobDetailsQuery';
import {
  ApplicationEvSectionType,
  ScheduleJobOutput,
  SectionEvInstallData,
} from '../../../__generated__/pgeplus-types';
import { titleCase } from '../../../util/string-utils';
import useFormState, {
  convertValidationRules,
} from '../../../hooks/useFormState';
import { validateRequiredAndValidCharacters } from '../../../util/form-validation';
import ScheduleForm from './ScheduleForm';
import Backdrop from '../../backdrop/Backdrop';
import { useMutation } from '@apollo/react-hooks';
import { scheduleJobMutaton } from '../../../queries/pge-plus/installerPortal.query';
import {
  convertTimeToAPStyle,
  getTimeRangeString,
  parseTimeRange,
} from '../../../util/date-utils';
import useUtil from '../../../hooks/pge-plus/useUtil';
import InstallerButton from '../../pge-plus-common/InstallerButton';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import InstallerPageHeader from '../../pge-plus-common/InstallerPageHeader';

interface Props {
  path?: string;
}

export type ScheduleFormState = {
  date?: string | undefined;
  time?: string | undefined;
};

const JobSchedule: FC<Props> = props => {
  const { jobId } = useParams();
  const [scheduledSuccess, setScheduledSuccess] = useState(false);
  const { wrapWithLoader } = useWrapWithLoader();
  const location = useLocation();

  const [scheduleJob] = useMutation<{
    scheduleJob: ScheduleJobOutput;
  }>(scheduleJobMutaton, {
    context: {
      pgePlus: true,
      installerPortal: true,
    },
  });

  const { content, richText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_RESCHEDULE_CANCEL,
  );

  const { loading, data, error } = useInstallerJobDetailsQuery(jobId);
  const { setErrorNotification } = useUtil();

  const installationInterval = data?.getJob.job.serviceTime
    ? getTimeRangeString(data?.getJob.job.serviceTime)
    : { date: '', timeRange: '' };

  const evInstallData = data?.getJob.application.details.find(
    section => section.sectionType === ApplicationEvSectionType.EvInstall,
  )?.data as SectionEvInstallData;

  const validateForm = () =>
    convertValidationRules<ScheduleFormState, { content: Map<string, string> }>(
      context => {
        const { values, content: t } = context;
        return {
          date: () => (!values.date ? t.get('EMPTY_DATE_ERROR') : null),
          time: validateRequiredAndValidCharacters.bind(
            null,
            t.get('EMPTY_TIME_ERROR_SLUG')!,
          ),
        };
      },
    );

  const formState = useFormState(
    {
      date: '',
      time: '',
    },
    {
      validate: validateForm(),
      validationContext: {
        content,
      },
    },
  );

  const getEvInstallData = (): SectionEvInstallData => {
    return {
      schedulePreference: evInstallData?.schedulePreference,
    };
  };

  const EV_INSTALLATION_DATA = getEvInstallData();
  const theme = useTheme();

  const onSubmit = wrapWithLoader(async (formValues: ScheduleFormState) => {
    try {
      const { data: scheduleJobData } = await scheduleJob({
        variables: {
          input: {
            jobId: jobId,
            serviceTime:
              formState.values.date && formState.values.time
                ? parseTimeRange(formState.values.time, formState.values.date)
                : '',
          },
        },
      });
      if (scheduleJobData?.scheduleJob?.job) {
        setScheduledSuccess(true);
      } else {
        setErrorNotification(true);
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const isFromReScheduleInstallationRoute = location.pathname.includes(
    'reschedule',
  );

  const pageHeader = scheduledSuccess
    ? isFromReScheduleInstallationRoute
      ? content.get('RESCHEDULE_CONFIRMATION_HEADER')
      : content.get('SCHEDULE_CONFIRMATION_HEADER')
    : isFromReScheduleInstallationRoute
    ? content.get('RESCHEDULE_INSTALLATION_HEADER')
    : content.get('SCHEDULE_INSTALLATION_HEADER');

  return (
    <Layout>
      {loading && <Backdrop forceOpen />}
      {data && (
        <>
          <Grid
            container
            css={{
              marginTop: 16,
              marginBottom: 96,
              justifyContent: 'center',
            }}
          >
            <Grid item container xs={12} md={7} css={{ rowGap: 30 }}>
              <Grid item xs={12}>
                <InstallerPageHeader
                  pageHeaderInfo={{
                    pageTitle: pageHeader,
                  }}
                  jobData={data?.getJob}
                  content={content}
                />
              </Grid>

              {!scheduledSuccess && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: '20px !important',
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '16px !important',
                        },
                      }}
                    >
                      {content.get('CUSTOMER_REQUEST')}
                    </Typography>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: '20px !important',
                        fontWeight: 700,
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '16px !important',
                        },
                      }}
                    >
                      {EV_INSTALLATION_DATA.schedulePreference?.dayOfWeek?.map(
                        (day, index, days) => {
                          if (index < days.length - 1) {
                            return `${titleCase(day)}, `;
                          } else {
                            return titleCase(day);
                          }
                        },
                      )}
                    </Typography>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: '20px !important',
                        fontWeight: 700,
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '16px !important',
                        },
                      }}
                    >
                      {EV_INSTALLATION_DATA.schedulePreference?.preferredServiceTime
                        ?.map(convertTimeToAPStyle)
                        .join(', ')}
                    </Typography>
                  </Grid>
                  {isFromReScheduleInstallationRoute && (
                    <Grid item xs={12}>
                      <Typography
                        variant={'body1'}
                        css={{
                          fontSize: '20px !important',
                          [theme.breakpoints.down('sm')]: {
                            fontSize: '16px !important',
                          },
                        }}
                      >
                        {content.get('REUSABLE_INSTALLATION_DATE_AND_TIME')}
                      </Typography>
                      <Typography
                        variant={'body1'}
                        css={{
                          fontSize: '20px !important',
                          fontWeight: 700,
                          [theme.breakpoints.down('sm')]: {
                            fontSize: '16px !important',
                          },
                        }}
                      >
                        {installationInterval?.date}
                      </Typography>
                      <Typography
                        variant={'body1'}
                        css={{
                          fontSize: '20px !important',
                          fontWeight: 700,
                          [theme.breakpoints.down('sm')]: {
                            fontSize: '16px !important',
                          },
                        }}
                      >
                        {installationInterval?.timeRange}
                      </Typography>
                    </Grid>
                  )}
                  <ScheduleForm
                    formState={formState}
                    content={content}
                    isFromReScheduleInstallationRoute={
                      isFromReScheduleInstallationRoute
                    }
                  />
                  <Grid item xs={12}>
                    <Box
                      mt={3}
                      css={{
                        display: 'flex',
                        justifyContent: 'center',
                        columnGap: 30,
                        [theme.breakpoints.down('sm')]: {
                          alignItems: 'center',
                          height: '96px',
                          backgroundColor: colors.cloudGrey,
                          position: 'fixed',
                          bottom: 0,
                          width: '100%',
                          left: 0,
                          boxShadow: `0px -3px 6px ${colors.boxShadowColor}`,
                        },
                      }}
                    >
                      <InstallerButton
                        color={'primary'}
                        variant={'outlined'}
                        onClick={() => {
                          formState.reset({
                            time: '',
                            date: '',
                          });
                          window.history.back();
                        }}
                      >
                        {content.get('REUSABLE_BUTTON_BACK')}
                      </InstallerButton>
                      <InstallerButton
                        type={'submit'}
                        color={'primary'}
                        variant={'contained'}
                        onClick={formState.submit(values => onSubmit(values))}
                      >
                        {content.get('REUSABLE_CONFIRM')}
                      </InstallerButton>
                    </Box>
                  </Grid>
                </>
              )}

              {scheduledSuccess && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: '20px !important',
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '16px !important',
                        },
                      }}
                    >
                      {content.get('NEW_INSTALLATION_DATE_TIME_COLON')}
                    </Typography>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: '20px !important',
                        fontWeight: 700,
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '16px !important',
                        },
                      }}
                    >
                      {formState.values.date}
                    </Typography>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: '20px !important',
                        fontWeight: 700,
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '16px !important',
                        },
                      }}
                    >
                      {convertTimeToAPStyle(formState.values.time)}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      mt={3}
                      css={{
                        display: 'flex',
                        justifyContent: 'center',
                        [theme.breakpoints.down('sm')]: {
                          alignItems: 'center',
                          height: '96px',
                          backgroundColor: colors.cloudGrey,
                          position: 'fixed',
                          bottom: 0,
                          width: '100%',
                          left: 0,
                          boxShadow: `0px -3px 6px ${colors.boxShadowColor}`,
                        },
                      }}
                    >
                      <InstallerButton
                        color={'primary'}
                        variant={'contained'}
                        onClick={() =>
                          void navigate(
                            ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING,
                          )
                        }
                      >
                        {content.get('REUSABLE_BACK_TO_HOME')}
                      </InstallerButton>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </Layout>
  );
};

export default JobSchedule;
